import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

export type MarketplaceConnection = components["schemas"]["ConnectionStatus"];

export const MARKETPLACE_CONNECTIONS_KEY = ["marketplace-connections"] as const;

/**
 * GET /v1/marketplace/connections — one entry per stored shop row (a
 * marketplace may carry several, at most one active) plus a synthesised
 * `not_connected` entry for a marketplace with no rows at all.
 *
 * This is the single seller source for the whole app (BE spec D5). It replaced
 * `useOrderSellers` (`DISTINCT seller_id FROM orders`), which could not offer a
 * shop that had been connected but had no orders yet, and which showed every
 * shop as the legacy constant `aloshop`.
 *
 * Lives in `src/api/` rather than inside the marketplace-connections feature
 * because the `promotion` and `orders` features both consume it, and features
 * may not import each other.
 */
export async function fetchMarketplaceConnections(): Promise<MarketplaceConnection[]> {
  const { data, error } = await apiClient.GET("/v1/marketplace/connections");
  if (error) throw error;
  return data.connections;
}

export function useMarketplaceConnections() {
  return useQuery({
    queryKey: MARKETPLACE_CONNECTIONS_KEY,
    queryFn: fetchMarketplaceConnections,
    // Shared across surfaces that don't need a live poll (orders filters,
    // export, promotion's seller dropdown) — just fresh enough that tab
    // switches don't feel stale, without hammering a per-shop-subquery
    // endpoint every 60s from every open dropdown. The connections page
    // itself opts back into the aggressive poll; see ConnectionsPage.tsx.
    staleTime: 30_000,
  });
}

/**
 * Connections of one marketplace that actually name a shop, active first.
 * The synthesised `not_connected` entry carries no `shop_id`, so it is never a
 * selectable seller.
 */
export function sellersForMarketplace(
  connections: MarketplaceConnection[],
  marketplace: string,
): MarketplaceConnection[] {
  return connections
    .filter((c) => c.marketplace === marketplace && !!c.shop_id)
    .sort((a, b) => Number(b.status === "active") - Number(a.status === "active"));
}
